/**
 * Set <title>, <meta name="description"> and <link rel="canonical"> per route.
 *
 * Restores the original homepage values on unmount so SPA navigation between
 * a product page and the homepage doesn't leave stale metadata.
 *
 * Why DOM-direct instead of react-helmet? Same reason as useStructuredData —
 * a 60KB dep for two attributes is overkill.
 */
import { useEffect } from 'react';

const DEFAULT_TITLE = 'SoleFinder - Find Your Perfect Shoe in 60 Seconds';
const DEFAULT_DESC = 'AI-powered shoe discovery across 34 trusted retailers in Australia, the UK and the US. Find your perfect fit in 60 seconds and compare live prices, colours and widths across 900+ shoes.';

function setMeta(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOg(property, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Manage <link rel="alternate" hreflang="..."> tags as a group keyed by data-key.
function setHreflang(alternates, key) {
  // Remove any previous hreflang group with this key
  document.head
    .querySelectorAll(`link[rel="alternate"][data-key="${key}"]`)
    .forEach((n) => n.remove());
  if (!alternates || !alternates.length) return;
  for (const { hreflang, href } of alternates) {
    if (!hreflang || !href) continue;
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    el.setAttribute('href', href);
    el.setAttribute('data-key', key);
    document.head.appendChild(el);
  }
}

export default function useSeoMeta({ title, description, canonical, ogImage, hreflang, hreflangKey = 'page' }) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.head.querySelector('meta[name="description"]')?.content;
    const prevOgTitle = document.head.querySelector('meta[property="og:title"]')?.content;
    const prevOgDesc = document.head.querySelector('meta[property="og:description"]')?.content;
    const prevOgImg = document.head.querySelector('meta[property="og:image"]')?.content;
    const prevCanonical = document.head.querySelector('link[rel="canonical"]')?.href;

    if (title) document.title = title;
    if (description) setMeta('description', description);
    if (title) setOg('og:title', title);
    if (description) setOg('og:description', description);
    if (ogImage) setOg('og:image', ogImage);
    if (canonical) setCanonical(canonical);
    if (hreflang) setHreflang(hreflang, hreflangKey);

    return () => {
      document.title = prevTitle || DEFAULT_TITLE;
      setMeta('description', prevDesc || DEFAULT_DESC);
      setOg('og:title', prevOgTitle || DEFAULT_TITLE);
      setOg('og:description', prevOgDesc || DEFAULT_DESC);
      if (prevOgImg) setOg('og:image', prevOgImg);
      if (prevCanonical) setCanonical(prevCanonical);
      // Always clean up our hreflang group on unmount
      document.head
        .querySelectorAll(`link[rel="alternate"][data-key="${hreflangKey}"]`)
        .forEach((n) => n.remove());
    };
  }, [title, description, canonical, ogImage, hreflang, hreflangKey]);
}
